import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, Heart, ListChecks, Plus, ChevronRight, Calendar, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { getWishlistsByUser, getWishItems, type Wishlist, type WishlistType } from "@/services/wishlists";
import CreateWishlistModal from "./CreateWishlistModal";
import { Button } from "@/components/ui/button";

const TYPE_CONFIG: Record<WishlistType, { icon: typeof Gift; label: string; gradient: string }> = {
  wishlist: { icon: Gift, label: "Wishlist", gradient: "from-primary/20 to-primary/5" },
  registry: { icon: Heart, label: "Registry", gradient: "from-secondary/30 to-secondary/10" },
  personal: { icon: ListChecks, label: "Personal", gradient: "from-muted to-muted/50" },
};

const WishlistSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<WishlistType>("wishlist");

  const loadWishlists = async () => {
    if (!user) return;
    const { data } = await getWishlistsByUser(user.id);
    setWishlists(data || []);

    // Load item counts for each wishlist
    if (data && data.length > 0) {
      const counts: Record<string, number> = {};
      await Promise.all(
        data.map(async (wl) => {
          const { data: items } = await getWishItems(wl.id);
          counts[wl.id] = items?.length || 0;
        })
      );
      setItemCounts(counts);
    }
  };

  useEffect(() => {
    loadWishlists();
  }, [user]);

  const typeEntries = Object.entries(TYPE_CONFIG) as [
    WishlistType,
    (typeof TYPE_CONFIG)["wishlist"],
  ][];

  const hasWishlists = wishlists.length > 0;

  return (
    <div className="space-y-10">
      {/* Create button — top right on desktop, inline on mobile */}
      {hasWishlists && (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setCreateType("wishlist");
              setCreateOpen(true);
            }}
            className="rounded-full shadow-sm"
            size="sm"
          >
            <Plus size={16} className="mr-1.5" />
            {t("wishlist.create", { defaultValue: "New List" })}
          </Button>
        </div>
      )}

      {/* Wishlist groups */}
      {typeEntries.map(([type, config]) => {
        const Icon = config.icon;
        const items = wishlists.filter((w) => w.type === type);
        if (items.length === 0) return null;

        return (
          <div key={type} className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <Icon size={16} className="text-primary/70" />
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                {config.label}
              </p>
              <span className="text-[11px] text-muted-foreground/60">· {items.length}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {items.map((wl) => {
                const count = itemCounts[wl.id] || 0;
                return (
                  <button
                    key={wl.id}
                    onClick={() => navigate(`/wishlists/${wl.id}`)}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card text-left transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {/* Cover area */}
                    <div className={`relative aspect-[2/1] w-full overflow-hidden bg-gradient-to-br ${config.gradient}`}>
                      {wl.cover_url ? (
                        <img
                          src={wl.cover_url}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 rounded-full bg-background/60 backdrop-blur-sm flex items-center justify-center">
                              <Icon size={22} className="text-primary/70" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Item count pill */}
                      <div className="absolute top-3 end-3 bg-background/80 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1.5">
                        <Sparkles size={10} className="text-primary" />
                        <span className="text-[10px] font-semibold text-foreground">
                          {count} {count === 1 ? "wish" : "wishes"}
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex flex-1 items-center gap-3 p-4">
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {wl.title}
                        </p>
                        {wl.description && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {wl.description}
                          </p>
                        )}
                        {wl.event_date && (
                          <div className="mt-1.5 flex items-center gap-1.5 text-muted-foreground/80">
                            <Calendar size={10} />
                            <p className="text-[10px] font-medium uppercase tracking-wide">
                              {new Date(wl.event_date).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                      <ChevronRight
                        size={18}
                        className="shrink-0 text-muted-foreground/40 transition-all duration-300 group-hover:text-primary group-hover:translate-x-0.5"
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Empty state */}
      {!hasWishlists && (
        <div className="flex flex-col items-center justify-center py-16 sm:py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-5">
            <Gift size={32} className="text-primary/60" />
          </div>
          <p className="font-serif text-xl font-semibold text-foreground mb-2">
            {t("wishlist.emptyTitle", { defaultValue: "Start your first wish list" })}
          </p>
          <p className="text-sm text-muted-foreground max-w-xs mb-8">
            {t("wishlist.emptyDesc", { defaultValue: "Create a list for birthdays, weddings, or just things you love — then share it with friends and family." })}
          </p>
          <Button
            onClick={() => {
              setCreateType("wishlist");
              setCreateOpen(true);
            }}
            className="rounded-full shadow-lg"
            size="lg"
          >
            <Plus size={18} className="mr-1.5" />
            {t("wishlist.create", { defaultValue: "Create Wishlist" })}
          </Button>
        </div>
      )}

      <CreateWishlistModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        type={createType}
        onTypeChange={setCreateType}
        onCreated={(wl) => {
          setCreateOpen(false);
          navigate(`/wishlists/${wl.id}`);
        }}
      />
    </div>
  );
};

export default WishlistSection;
